import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { sanitizeLeagueHtml } from "@/lib/sanitize-html";

const DAY_MS = 24 * 60 * 60 * 1000;

type Stored = { version: string; dismissedUntil?: number; hidden?: boolean };

const storageKey = (leagueId: string) => `league-welcome-popup:${leagueId}`;

const readState = (leagueId: string): Stored | null => {
  try {
    const raw = localStorage.getItem(storageKey(leagueId));
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
};

const writeState = (leagueId: string, state: Stored) => {
  try {
    localStorage.setItem(storageKey(leagueId), JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

export const LeagueWelcomePopup = ({
  leagueId,
  leagueName,
  enabled,
  html,
  updatedAt,
}: {
  leagueId: string;
  leagueName: string;
  enabled?: boolean | null;
  html?: string | null;
  updatedAt?: string | null;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const version = updatedAt ?? "";
  const clean = useMemo(() => (html ? sanitizeLeagueHtml(html).clean : ""), [html]);

  useEffect(() => {
    if (!enabled || !clean.trim()) return;
    const state = readState(leagueId);
    if (state && state.version === version) {
      if (state.hidden) return;
      if (state.dismissedUntil && state.dismissedUntil > Date.now()) return;
    }
    setOpen(true);
  }, [leagueId, enabled, clean, version]);

  const closeFor24h = () => {
    writeState(leagueId, { version, dismissedUntil: Date.now() + DAY_MS });
    setOpen(false);
  };

  const neverShow = () => {
    writeState(leagueId, { version, hidden: true });
    setOpen(false);
  };

  if (!enabled || !clean.trim()) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeFor24h(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{leagueName}</DialogTitle>
        </DialogHeader>
        <div
          className="prose prose-sm dark:prose-invert max-w-none break-words max-h-[55vh] overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: clean }}
        />
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
          <Button onClick={closeFor24h} className="rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0 hover:opacity-95">
            {t("common.close")}
          </Button>
          <button
            type="button"
            onClick={neverShow}
            className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            {t("league.popupDontShowAgain")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
